import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../../api/api";
import { APIError } from "../../../constants/types";
import { ISOVFetchParams, ISOVImportPayload, ISOVImportRecord } from "./businessPolicySOV.types";
import { showMessage } from "../../messages/messagesActions";
import {
  buildBinderRequest,
  ILeadScheduleScope,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-additional-tabs
// 30s client-side bound so lead-scoped fetch surfaces the error + retry path
// deterministically (Requirements 4.5, 4.9).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

/**
 * Fetches the list of SOV import records with pagination, date range, and filter params.
 * GET /import-utility/get-sov-import
 */
export const fetchSOVList = createAsyncThunk<
  {
    data: ISOVImportRecord[];
    totalRecords: number;
  },
  ISOVFetchParams,
  { rejectValue: APIError }
>(
  "businessPolicySOV/fetchSOVList",
  async (params, { rejectWithValue, dispatch }) => {
    try {
      let url = `/import-utility/get-sov-import?customer_policy_id=${params.customer_policy_id}&pageNo=${params.pageNo}&pageLimit=${params.pageLimit}&itemPerPage=${params.itemPerPage}`;

      if (params.from_date) {
        url += `&from_date=${params.from_date}`;
      }
      if (params.to_date) {
        url += `&to_date=${params.to_date}`;
      }
      if (params.advanceFilter && Object.keys(params.advanceFilter).length > 0) {
        url += `&advanceFilter=${JSON.stringify(params.advanceFilter)}`;
      }

      const res = await api.get(url);
      const data: ISOVImportRecord[] = res.data.data;
      const totalRecords: number = res.data.totalRecords;

      return {
        data: data || [],
        totalRecords: totalRecords || 0,
      };
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);


/**
 * Feature: lead-binder-additional-tabs
 * Lead-scoped SOV list fetch. Keyed by `lead_id` (never `customer_policy_id`), routed
 * through the centralized endpoint map. On failure/timeout it rejects, flipping the list
 * to the Failed load state (retry surfaced by the sub-tab) and shows an error message
 * (Requirements 4.1, 4.5, 4.9). Mirrors `fetchLeadBusinessRiskListAsync`.
 * GET /import-utility/get-sov-import?lead_id={leadId}
 */
export const fetchLeadSOVListAsync = createAsyncThunk<
  {
    data: ISOVImportRecord[];
    totalRecords: number;
  },
  ILeadScheduleScope,
  { rejectValue: APIError }
>(
  "businessPolicySOV/fetchLeadSOVList",
  async (scope, { rejectWithValue, dispatch }) => {
    try {
      const request = buildBinderRequest(
        "sov",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const res = await api.get(request.url, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: ISOVImportRecord[] = res.data.data;
      const totalRecords: number = res.data.totalRecords;

      return {
        data: data || [],
        totalRecords: totalRecords || 0,
      };
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "SOV binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Imports an SOV file (Excel or PDF).
 * POST /import-utility/import-sov
 * For Excel: includes start_row and end_row in payload.
 * For PDF: omits start_row and end_row.
 */
export const importSOV = createAsyncThunk<
  {
    data: any;
    message: string;
  },
  ISOVImportPayload,
  { rejectValue: APIError }
>(
  "businessPolicySOV/importSOV",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const requestBody: ISOVImportPayload = {
        file_path: payload.file_path,
        file_type: payload.file_type,
      };

      // Only include start_row and end_row for Excel file types
      if (payload.file_type === "Excel") {
        requestBody.start_row = payload.start_row;
        requestBody.end_row = payload.end_row;
      }

      const res = await api.post("/import-utility/import-sov", requestBody);

      return {
        data: res.data.data,
        message: res.data.message || "SOV imported successfully",
      };
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Extracts data from a PDF file for SOV import.
 * POST endpoint - placeholder URL (to be updated with actual endpoint later).
 */
export const extractSOVPdfData = createAsyncThunk<
  {
    headers: string[];
    sampleData: Record<string, string[]>;
  },
  { file_path: string },
  { rejectValue: APIError }
>(
  "businessPolicySOV/extractSOVPdfData",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      // TODO: Replace with actual PDF extraction endpoint when available
      const res = await api.post("", payload);

      return {
        headers: res.data.data?.headers || [],
        sampleData: res.data.data?.sampleData || {},
      };
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Fetches column mapping suggestions for SOV import.
 * POST endpoint - placeholder URL (to be updated with actual endpoint later).
 */
export const fetchSOVColumnMapping = createAsyncThunk<
  {
    mappings: Array<{ headerName: string; suggestedAttribute: string | null }>;
  },
  { headers: string[] },
  { rejectValue: APIError }
>(
  "businessPolicySOV/fetchSOVColumnMapping",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      // TODO: Replace with actual column mapping endpoint when available
      const res = await api.post("", payload);

      return {
        mappings: res.data.data?.mappings || [],
      };
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);
